import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Activity, Download, LogOut, MessageSquare, ShieldCheck, Trash2, Watch } from "lucide-react";
import { api, DEVICE_TYPE_LABELS, downloadExport, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

function Section({ title, testId, children, action }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid={testId}>
      <div className="mb-5 flex items-center justify-between">
        <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [devices, setDevices] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/v1/account").then(({ data }) => setAccount(data)).catch(() => {});
    api.get("/v1/devices").then(({ data }) => setDevices(data.devices)).catch(() => {});
    api.get("/v1/integrations").then(({ data }) => setIntegrations(data.integrations)).catch(() => {});
  }, []);

  const active = user?.setup?.completed;
  const watch = devices.find((d) => d.device_type === "apple_watch");
  const chatgpt = integrations.find((i) => i.provider === "chatgpt");

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadExport();
      toast.success("Your MIA data export has been downloaded.");
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="dashboard">
        <h1 className="font-display text-3xl font-medium tracking-tight">MIA</h1>

        <Section title="Status" testId="dashboard-status-section">
          <div className="flex items-center gap-3">
            <Activity className="h-5 w-5 text-muted-foreground" />
            <span className="text-lg font-medium">MIA</span>
            {active ? (
              <Badge variant="secondary" className="gap-1" data-testid="dashboard-status-active">
                <ShieldCheck className="h-3 w-3" /> Active
              </Badge>
            ) : (
              <>
                <Badge variant="outline" data-testid="dashboard-status-incomplete">Setup incomplete</Badge>
                <Button asChild variant="link" size="sm" data-testid="dashboard-finish-setup-link">
                  <Link to="/setup">Finish setup</Link>
                </Button>
              </>
            )}
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Your memory infrastructure is running quietly in the background.
          </p>
        </Section>

        <Section title="Devices" testId="dashboard-devices-section"
                 action={<Button asChild variant="outline" size="sm" data-testid="manage-devices-button"><Link to="/devices">Manage devices</Link></Button>}>
          <div className="flex items-center gap-3">
            <Watch className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">{watch ? watch.device_name : "Apple Watch"}</p>
              <p className="text-xs text-muted-foreground" data-testid="dashboard-device-status">
                {watch ? `${DEVICE_TYPE_LABELS[watch.device_type]} · Last seen ${timeAgo(watch.last_seen_at)}` : "Not connected"}
              </p>
            </div>
            <Badge variant={watch ? "secondary" : "outline"} className="ml-auto">
              {watch ? "Active" : "Not connected"}
            </Badge>
          </div>
        </Section>

        <Section title="Integrations" testId="dashboard-integrations-section"
                 action={<Button asChild variant="outline" size="sm" data-testid="manage-integrations-button"><Link to="/integrations">Manage integrations</Link></Button>}>
          <div className="flex items-center gap-3">
            <MessageSquare className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">ChatGPT</p>
              <p className="text-xs text-muted-foreground">Ask ChatGPT about what you captured with MIA.</p>
            </div>
            <Badge variant="outline" className="ml-auto" data-testid="dashboard-chatgpt-status">
              {chatgpt?.status === "coming_soon" ? "Coming Soon" : chatgpt?.status || "Coming Soon"}
            </Badge>
          </div>
        </Section>

        <Section title="Account" testId="dashboard-account-section">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Subscription</p>
                <p className="text-sm font-medium" data-testid="dashboard-subscription-label">
                  {account?.user?.subscription?.label || "MIA Test — Free"}
                </p>
              </div>
              <Badge variant="secondary" data-testid="dashboard-subscription-badge">Free test</Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Signed in as</p>
              <p className="text-sm font-medium" data-testid="dashboard-account-email">{user?.email}</p>
            </div>
          </div>
        </Section>

        <Section title="Privacy & Data" testId="dashboard-privacy-section">
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" size="sm" data-testid="export-data-button" onClick={handleExport} disabled={exporting}>
              <Download className="mr-2 h-4 w-4" /> {exporting ? "Exporting…" : "Export my data"}
            </Button>
            <Button asChild variant="outline" size="sm" data-testid="delete-data-button">
              <Link to="/privacy"><Trash2 className="mr-2 h-4 w-4" /> Delete my data</Link>
            </Button>
            <Button asChild variant="outline" size="sm" data-testid="delete-account-button">
              <Link to="/privacy" className="text-destructive"><Trash2 className="mr-2 h-4 w-4" /> Delete account</Link>
            </Button>
            <Button variant="ghost" size="sm" data-testid="sign-out-button" onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </Button>
          </div>
        </Section>
      </div>
    </Layout>
  );
}
